import React, { useState } from 'react';
import {
  FileText,
  DollarSign,
  Calendar,
  Building,
  Users,
  Search,
  Filter,
  Download,
  Printer,
  Eye,
  Edit,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Plus,
  MoreHorizontal,
  TrendingUp,
  TrendingDown,
  Target,
  Activity
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import StatCard from '../../components/StatCard';

interface InvoiceItem {
  id: string;
  medicationId: string;
  medicationName: string;
  strength: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  pbsItem: boolean;
  patientCopayment?: number;
  governmentBenefit?: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  customerType: 'aged_care' | 'ndis' | 'retail' | 'hospital' | 'clinic';
  facilityName?: string;
  abn?: string;
  billingAddress: string;
  
  // Invoice Details
  issueDate: string;
  dueDate: string;
  serviceDate: string;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'cancelled';
  
  // Financial Information
  subtotal: number;
  gstAmount: number;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
  
  // Items and Services
  items: InvoiceItem[];
  totalItems: number;
  
  // Payment Information
  paymentTerms: string;
  paymentMethod?: string;
  paymentDate?: string;
  
  // Additional Information
  orderNumbers: string[];
  notes: string;
  internalNotes: string;
  
  // Compliance
  pbsClaimNumber?: string;
  ndisClaimNumber?: string;
  authorityNumbers: string[];
}

const InvoiceManagement: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<'all' | 'facility' | 'retail' | 'analytics'>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCustomerType, setSelectedCustomerType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateRange, setDateRange] = useState<string>('current_month');
  const [showInvoiceDetails, setShowInvoiceDetails] = useState<string | null>(null);

  // Mock invoice data
  const invoices: Invoice[] = [
    {
      id: 'inv-001',
      invoiceNumber: 'INV-2024-0915-001',
      customerId: 'cust-ac-001',
      customerName: 'Sunnydale Aged Care Facility',
      customerType: 'aged_care',
      facilityName: 'Sunnydale Aged Care Facility',
      abn: '12 345 678 901',
      billingAddress: '123 Peaceful Drive, Sunnydale NSW 2000',
      issueDate: '2024-09-15T00:00:00Z',
      dueDate: '2024-09-29T00:00:00Z',
      serviceDate: '2024-09-14T00:00:00Z',
      status: 'sent',
      subtotal: 2847.50,
      gstAmount: 284.75,
      totalAmount: 3132.25,
      paidAmount: 0,
      outstandingAmount: 3132.25,
      items: [
        {
          id: 'item-001',
          medicationId: 'med-001',
          medicationName: 'Atorvastatin 20mg',
          strength: '20mg',
          quantity: 150,
          unitPrice: 32.90,
          totalPrice: 4935.00,
          pbsItem: true,
          patientCopayment: 1020.00,
          governmentBenefit: 3915.00
        },
        {
          id: 'item-002',
          medicationId: 'med-004',
          medicationName: 'Metformin 500mg',
          strength: '500mg',
          quantity: 200,
          unitPrice: 14.90,
          totalPrice: 2980.00,
          pbsItem: true,
          patientCopayment: 1360.00,
          governmentBenefit: 1620.00
        }
      ],
      totalItems: 2,
      paymentTerms: 'Net 14 days',
      orderNumbers: ['AC-2024-0915-001', 'AC-2024-0914-003'],
      notes: 'Webster packs for 28 days supply. All medications dispensed according to prescriptions.',
      internalNotes: 'Regular monthly order. Customer has good payment history.',
      pbsClaimNumber: 'PBS-2024-091501',
      authorityNumbers: ['AUTH-12345', 'AUTH-67890']
    },
    {
      id: 'inv-002',
      invoiceNumber: 'INV-2024-0915-002',
      customerId: 'cust-ndis-001',
      customerName: 'NDIS Support Services',
      customerType: 'ndis',
      facilityName: 'NDIS Support Services',
      abn: '98 765 432 109',
      billingAddress: '456 Care Street, Sydney NSW 2001',
      issueDate: '2024-09-15T00:00:00Z',
      dueDate: '2024-09-29T00:00:00Z',
      serviceDate: '2024-09-15T00:00:00Z',
      status: 'paid',
      subtotal: 1245.80,
      gstAmount: 124.58,
      totalAmount: 1370.38,
      paidAmount: 1370.38,
      outstandingAmount: 0,
      items: [
        {
          id: 'item-003',
          medicationId: 'med-014',
          medicationName: 'Sertraline 50mg',
          strength: '50mg',
          quantity: 60,
          unitPrice: 38.90,
          totalPrice: 2334.00,
          pbsItem: true,
          patientCopayment: 408.00,
          governmentBenefit: 1926.00
        }
      ],
      totalItems: 1,
      paymentTerms: 'NDIS direct billing',
      paymentMethod: 'Electronic Transfer',
      paymentDate: '2024-09-16T00:00:00Z',
      orderNumbers: ['NDIS-2024-0915-002'],
      notes: 'Individual patient packs with compliance aids as per NDIS plan.',
      internalNotes: 'NDIS direct billing - payment received automatically.',
      ndisClaimNumber: 'NDIS-2024-091502',
      authorityNumbers: []
    },
    // Retail invoices (grouped)
    {
      id: 'inv-003',
      invoiceNumber: 'INV-2024-0915-003',
      customerId: 'retail-group',
      customerName: 'Retail Customers - Daily Summary',
      customerType: 'retail',
      billingAddress: 'Various walk-in customers',
      issueDate: '2024-09-15T00:00:00Z',
      dueDate: '2024-09-15T00:00:00Z',
      serviceDate: '2024-09-15T00:00:00Z',
      status: 'paid',
      subtotal: 1847.30,
      gstAmount: 184.73,
      totalAmount: 2032.03,
      paidAmount: 2032.03,
      outstandingAmount: 0,
      items: [
        {
          id: 'item-004',
          medicationId: 'med-010',
          medicationName: 'Paracetamol 500mg',
          strength: '500mg',
          quantity: 45,
          unitPrice: 8.95,
          totalPrice: 402.75,
          pbsItem: false
        },
        {
          id: 'item-005',
          medicationId: 'med-006',
          medicationName: 'Ventolin HFA 100mcg',
          strength: '100mcg/dose',
          quantity: 12,
          unitPrice: 22.50,
          totalPrice: 270.00,
          pbsItem: true,
          patientCopayment: 81.60,
          governmentBenefit: 188.40
        }
      ],
      totalItems: 57, // Total retail transactions for the day
      paymentTerms: 'Immediate payment',
      paymentMethod: 'Mixed (Cash/Card/EFTPOS)',
      paymentDate: '2024-09-15T00:00:00Z',
      orderNumbers: ['RTL-2024-0915-001', 'RTL-2024-0915-002', 'RTL-2024-0915-003'],
      notes: 'Daily retail summary - 57 individual transactions',
      internalNotes: 'Good retail day. High volume of cold/flu medications.',
      authorityNumbers: []
    },
    {
      id: 'inv-004',
      invoiceNumber: 'INV-2024-0914-001',
      customerId: 'cust-hosp-001',
      customerName: 'Sydney General Hospital',
      customerType: 'hospital',
      facilityName: 'Sydney General Hospital',
      abn: '11 222 333 444',
      billingAddress: '789 Hospital Road, Sydney NSW 2000',
      issueDate: '2024-09-14T00:00:00Z',
      dueDate: '2024-09-28T00:00:00Z',
      serviceDate: '2024-09-13T00:00:00Z',
      status: 'overdue',
      subtotal: 5420.80,
      gstAmount: 542.08,
      totalAmount: 5962.88,
      paidAmount: 0,
      outstandingAmount: 5962.88,
      items: [
        {
          id: 'item-006',
          medicationId: 'med-011',
          medicationName: 'OxyContin 10mg',
          strength: '10mg',
          quantity: 20,
          unitPrice: 165.90,
          totalPrice: 3318.00,
          pbsItem: true,
          patientCopayment: 136.00,
          governmentBenefit: 3182.00
        }
      ],
      totalItems: 1,
      paymentTerms: 'Net 30 days',
      orderNumbers: ['HOSP-2024-0913-001'],
      notes: 'S8 controlled substances - all documentation provided.',
      internalNotes: 'Follow up required - invoice overdue by 1 day.',
      authorityNumbers: ['S8-AUTH-98765']
    }
  ];

  // Analytics data
  const invoiceAnalytics = {
    totalInvoices: invoices.length,
    totalValue: invoices.reduce((sum, inv) => sum + inv.totalAmount, 0),
    paidValue: invoices.reduce((sum, inv) => sum + inv.paidAmount, 0),
    outstandingValue: invoices.reduce((sum, inv) => sum + inv.outstandingAmount, 0),
    averageInvoiceValue: invoices.reduce((sum, inv) => sum + inv.totalAmount, 0) / invoices.length,
    
    byCustomerType: {
      aged_care: invoices.filter(inv => inv.customerType === 'aged_care').reduce((sum, inv) => sum + inv.totalAmount, 0),
      ndis: invoices.filter(inv => inv.customerType === 'ndis').reduce((sum, inv) => sum + inv.totalAmount, 0),
      retail: invoices.filter(inv => inv.customerType === 'retail').reduce((sum, inv) => sum + inv.totalAmount, 0),
      hospital: invoices.filter(inv => inv.customerType === 'hospital').reduce((sum, inv) => sum + inv.totalAmount, 0)
    },
    
    byStatus: {
      paid: invoices.filter(inv => inv.status === 'paid').length,
      sent: invoices.filter(inv => inv.status === 'sent').length,
      overdue: invoices.filter(inv => inv.status === 'overdue').length,
      draft: invoices.filter(inv => inv.status === 'draft').length
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'viewed': return 'bg-purple-100 text-purple-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCustomerTypeColor = (type: string) => {
    switch (type) {
      case 'aged_care': return 'bg-purple-100 text-purple-800';
      case 'ndis': return 'bg-green-100 text-green-800';
      case 'retail': return 'bg-blue-100 text-blue-800';
      case 'hospital': return 'bg-orange-100 text-orange-800';
      case 'clinic': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesTab = selectedTab === 'all' || 
      (selectedTab === 'facility' && ['aged_care', 'ndis', 'hospital', 'clinic'].includes(invoice.customerType)) ||
      (selectedTab === 'retail' && invoice.customerType === 'retail');
    
    const matchesStatus = selectedStatus === 'all' || invoice.status === selectedStatus;
    const matchesCustomerType = selectedCustomerType === 'all' || invoice.customerType === selectedCustomerType;
    const matchesSearch = searchTerm === '' || 
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesTab && matchesStatus && matchesCustomerType && matchesSearch;
  });


  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Invoice Management
          </h1>
          <p className="text-gray-600 mt-2">Comprehensive billing and invoice processing system</p>
        </div>
        <div className="flex space-x-3">
          <button className="group bg-white/80 backdrop-blur-md text-gray-700 px-6 py-3 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center space-x-2 border border-gray-200">
            <Download className="h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
            <span>Export</span>
          </button>
          <button className="group bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2">
            <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform duration-200" />
            <span>New Invoice</span>
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Invoices"
          value={invoiceAnalytics.totalInvoices}
          subtitle="This month"
          icon={<FileText className="h-6 w-6" />}
          trend="vs last month"
          trendValue={8.2}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Total Value"
          value={`$${invoiceAnalytics.totalValue.toLocaleString()}`}
          subtitle="Invoice value"
          icon={<DollarSign className="h-6 w-6" />}
          trend="vs last month"
          trendValue={12.5}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Outstanding"
          value={`$${invoiceAnalytics.outstandingValue.toLocaleString()}`}
          subtitle="Pending payment"
          icon={<Clock className="h-6 w-6" />}
          trend="vs last month"
          trendValue={-5.3}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
        <StatCard
          title="Average Invoice"
          value={`$${invoiceAnalytics.averageInvoiceValue.toLocaleString()}`}
          subtitle="Per invoice"
          icon={<Target className="h-6 w-6" />}
          trend="vs last month"
          trendValue={3.8}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
      </div>

      {/* Tabs and Filters */}
      <div className="group bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/30 p-8 hover:shadow-2xl transition-all duration-500">
        {/* Tabs */}
        <div className="flex space-x-2 mb-8">
          {[
            { id: 'all', label: 'All Invoices', count: invoices.length },
            { id: 'facility', label: 'Facility Invoices', count: invoices.filter(inv => ['aged_care', 'ndis', 'hospital', 'clinic'].includes(inv.customerType)).length },
            { id: 'retail', label: 'Retail Invoices', count: invoices.filter(inv => inv.customerType === 'retail').length },
            { id: 'analytics', label: 'Analytics', count: 0 }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as any)}
              className={`group px-6 py-3 rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 ${
                selectedTab === tab.id
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 border border-gray-200'
              }`}
            >
              <span className="flex items-center space-x-2">
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    selectedTab === tab.id
                      ? 'bg-white/20 text-white'
                      : 'bg-purple-100 text-purple-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>

        {/* Filters */}
        {selectedTab !== 'analytics' && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="viewed">Viewed</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={selectedCustomerType}
              onChange={(e) => setSelectedCustomerType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Customer Types</option>
              <option value="aged_care">Aged Care</option>
              <option value="ndis">NDIS</option>
              <option value="retail">Retail</option>
              <option value="hospital">Hospital</option>
              <option value="clinic">Clinic</option>
            </select>

            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="current_month">Current Month</option>
              <option value="last_month">Last Month</option>
              <option value="last_3_months">Last 3 Months</option>
              <option value="current_year">Current Year</option>
            </select>

            <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Filter className="h-4 w-4" />
              <span>More Filters</span>
            </button>
          </div>
        )}
      </div>

      {/* Content based on selected tab */}
      {selectedTab === 'analytics' ? (
        /* Analytics View */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Revenue by Customer Type */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Revenue by Customer Type</h3>
            <div className="space-y-4">
              {Object.entries(invoiceAnalytics.byCustomerType).map(([type, value]) => (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCustomerTypeColor(type)}`}>
                      {type.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">${value.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">
                      {((value / invoiceAnalytics.totalValue) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Invoice Status Distribution */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Invoice Status Distribution</h3>
            <div className="space-y-4">
              {Object.entries(invoiceAnalytics.byStatus).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(status)}`}>
                      {status.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{count}</p>
                    <p className="text-sm text-gray-600">
                      {((count / invoiceAnalytics.totalInvoices) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* Invoice List View */
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Invoice</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Customer</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Type</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                        <p className="text-sm text-gray-600">Due: {format(new Date(invoice.dueDate), 'dd/MM/yyyy')}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{invoice.customerName}</p>
                        {invoice.facilityName && (
                          <p className="text-sm text-gray-600">{invoice.facilityName}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCustomerTypeColor(invoice.customerType)}`}>
                        {invoice.customerType.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {format(new Date(invoice.issueDate), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">${invoice.totalAmount.toLocaleString()}</p>
                        {invoice.outstandingAmount > 0 && (
                          <p className="text-sm text-red-600">Outstanding: ${invoice.outstandingAmount.toLocaleString()}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                        {invoice.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShowInvoiceDetails(invoice.id)}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <Printer className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <Send className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Details Modal */}
      {showInvoiceDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Invoice Details</h3>
              <button
                onClick={() => setShowInvoiceDetails(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            {/* Invoice details content would go here */}
            <div className="text-center py-8">
              <p className="text-gray-600">Invoice details view would be implemented here</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceManagement;
